"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";

export type PopoverPlacement =
  | "bottom-start"
  | "bottom-end"
  | "bottom-center"
  | "top-start"
  | "top-end"
  | "top-center";

export interface PopoverProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  placement?: PopoverPlacement;
  /** Gap (px) between anchor and popover. */
  offset?: number;
  className?: string;
  /** Nom accessible du `role="dialog"`. Sans lui, un lecteur d'écran annonce
   *  « dialogue » sans dire lequel. */
  ariaLabel?: string;
  children: React.ReactNode;
}

interface Position {
  top: number;
  left: number;
}

/** Marge minimale conservée entre le popover et le bord de l'écran. */
const VIEWPORT_MARGIN = 8;

function computePosition(
  anchor: DOMRect,
  popover: DOMRect,
  placement: PopoverPlacement,
  offset: number,
): Position {
  const isTop = placement.startsWith("top");
  const top = isTop
    ? anchor.top - popover.height - offset
    : anchor.bottom + offset;

  let left = anchor.left;

  if (placement.endsWith("-end")) {
    left = anchor.right - popover.width;
  } else if (placement.endsWith("-center")) {
    left = anchor.left + anchor.width / 2 - popover.width / 2;
  }

  // Le placement suit l'ancre, mais sur un écran étroit un panneau large finit
  // hors champ — et comme `body` est en `overflow:hidden`, ce qui sort n'est
  // plus atteignable du tout. On le ramène donc dans le cadre. Un popover qui
  // tient déjà à l'écran n'est pas déplacé : le desktop est inchangé.
  const maxLeft = window.innerWidth - popover.width - VIEWPORT_MARGIN;
  const clampedLeft = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));

  return { top, left: clampedLeft };
}

export function Popover({
  anchorRef,
  open,
  onClose,
  placement = "bottom-start",
  offset = 8,
  className,
  ariaLabel,
  children,
}: PopoverProps) {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;

    if (!anchor || !popover) {
      return;
    }

    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const next = computePosition(anchorRect, popoverRect, placement, offset);
    setPosition(next);
  }, [anchorRef, placement, offset]);

  // Initial position + animation entry frame on open.
  useLayoutEffect(() => {
    if (!open) {
      setEntered(false);
      setPosition(null);

      return;
    }

    updatePosition();
    const raf = requestAnimationFrame(() => {
      setEntered(true);
    });

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [open, updatePosition]);

  // Reposition on scroll / resize while open.
  useEffect(() => {
    if (!open) {
      return;
    }

    const handle = () => {
      updatePosition();
    };

    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);

    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
    };
  }, [open, updatePosition]);

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (!target) {
        return;
      }

      const popover = popoverRef.current;
      const anchor = anchorRef.current;

      if (popover && popover.contains(target)) {
        return;
      }

      if (anchor && anchor.contains(target)) {
        return;
      }

      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !mounted) {
    return null;
  }

  const isTop = placement.startsWith("top");
  const translateClass = entered
    ? "translate-y-0 opacity-100"
    : isTop
      ? "translate-y-1 opacity-0"
      : "-translate-y-1 opacity-0";

  const baseClass = [
    "fixed z-50 rounded-xl border border-border bg-bg-surface shadow-2xl",
    "transition duration-150 ease-out",
    translateClass,
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const style: React.CSSProperties = {
    top: position?.top ?? -9999,
    left: position?.left ?? -9999,
    visibility: position ? "visible" : "hidden",
    boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
  };

  return createPortal(
    <div
      ref={popoverRef}
      className={baseClass}
      style={style}
      role="dialog"
      aria-label={ariaLabel}
    >
      {children}
    </div>,
    document.body,
  );
}
